import React, { createContext, useContext, useState, useEffect } from 'react';
import { login as apiLogin, getMe, getRolePermissions } from '../lib/api';

const AuthContext = createContext(null);

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mustChangePassword, setMustChangePassword] = useState(false);
  const [permissions, setPermissions] = useState(null);

  const loadPermissions = async (role) => {
    if (!role || role === 'admin_kadolo') {
      setPermissions(null); // Admin a tous les droits
      return;
    }
    try {
      const res = await getRolePermissions(role);
      setPermissions(res.data);
    } catch {
      setPermissions(null);
    }
  };

  useEffect(() => {
    const initAuth = async () => {
      const token = localStorage.getItem('kadolo_token');
      const savedUser = localStorage.getItem('kadolo_user');
      
      if (token && savedUser) {
        try {
          const parsedUser = JSON.parse(savedUser);
          setUser(parsedUser);
          setMustChangePassword(parsedUser.must_change_password || false);
          const response = await getMe();
          setUser(response.data);
          setMustChangePassword(response.data.must_change_password || false);
          localStorage.setItem('kadolo_user', JSON.stringify(response.data));
          await loadPermissions(response.data.role);
        } catch (error) {
          console.error('Auth verification failed:', error);
          localStorage.removeItem('kadolo_token');
          localStorage.removeItem('kadolo_user');
          setUser(null);
          setPermissions(null);
          setMustChangePassword(false);
        }
      }
      setLoading(false);
    };

    initAuth();
  }, []);

  const login = async (credentials) => {
    const response = await apiLogin(credentials);
    const { access_token, user: userData } = response.data;
    
    localStorage.setItem('kadolo_token', access_token);
    localStorage.setItem('kadolo_user', JSON.stringify(userData));
    setUser(userData);
    setMustChangePassword(userData.must_change_password || false);
    await loadPermissions(userData.role);
    
    return userData;
  };

  const logout = () => {
    localStorage.removeItem('kadolo_token');
    localStorage.removeItem('kadolo_user');
    setUser(null);
    setPermissions(null);
    setMustChangePassword(false);
  };

  const clearMustChangePassword = () => {
    setMustChangePassword(false);
    if (user) {
      const updatedUser = { ...user, must_change_password: false };
      setUser(updatedUser);
      localStorage.setItem('kadolo_user', JSON.stringify(updatedUser));
    }
  };

  const hasPermission = (module, action) => {
    if (!user) return false;
    if (user.role === 'admin_kadolo') return true;
    if (!permissions) return false;
    const mod = permissions[module];
    if (!mod || typeof mod !== 'object') return false;
    return mod[action] === true;
  };

  const getToken = () => localStorage.getItem('kadolo_token');

  const value = {
    user,
    loading,
    login,
    logout,
    isAuthenticated: !!user,
    mustChangePassword,
    clearMustChangePassword,
    token: getToken(),
    getToken,
    permissions,
    hasPermission,
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
};

export default AuthContext;
